/** Google kimliği doğrulandıktan sonra, uygulama oturumundan önceki onay kapısı. */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Checkbox, LoadingBlock } from '@/components/ui';
import { api, ApiRequestError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';

interface PendingConsent {
  pending: true;
  email: string;
  displayName: string;
  client: 'web' | 'desktop';
}

interface ConsentResult {
  client: 'web' | 'desktop';
  accessToken: string;
  refreshToken?: string;
  next: string;
  requiresTwoFactor?: boolean;
  twoFactorToken?: string;
}

export default function GoogleConsent() {
  const navigate = useNavigate();
  const { completeExternalSession } = useAuth();
  const { language, t } = useI18n();
  const [pending, setPending] = useState<PendingConsent | null>(null);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [kvkkRead, setKvkkRead] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const pendingToken = params.get('istek') ?? '';
    // Tek kullanımlık anahtar adres geçmişinde gereğinden uzun kalmasın.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (!pendingToken) {
      setError(t('auth.googlePendingMissing'));
      setLoading(false);
      return;
    }
    setToken(pendingToken);
    void api.post<PendingConsent>('/auth/google/consent/status', { token: pendingToken })
      .then(setPending)
      .catch((err) => setError(err instanceof ApiRequestError ? err.message : t('auth.googleInfoFailed')))
      .finally(() => setLoading(false));
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!kvkkRead || !termsAccepted) {
      setError(t('auth.reviewBoth'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<ConsentResult>('/auth/google/consent', { token, kvkkRead, termsAccepted, language });
      if (result.requiresTwoFactor && result.twoFactorToken) {
        navigate('/giris', { replace: true, state: { twoFactorToken: result.twoFactorToken, from: result.next } });
        return;
      }
      if (result.client === 'desktop' && result.refreshToken) {
        const target = new URL('cortex://auth/callback');
        target.searchParams.set('accessToken', result.accessToken);
        target.searchParams.set('refreshToken', result.refreshToken);
        window.location.href = target.toString();
        return;
      }
      const me = await completeExternalSession(result.accessToken);
      navigate(result.next || (me.nav.includes('admin') ? '/yonetim' : '/'), { replace: true });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t('auth.consentFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingBlock rows={4} label={t('auth.googleVerifying')} />;
  if (!pending) return <>
    <div><h1 className="auth__title">{t('auth.googleFailed')}</h1><p className="auth__sub">{error ?? t('auth.googlePendingMissing')}</p></div>
    <Link className="btn btn--primary btn--lg auth__wide-action" to="/giris">{t('auth.backToSignIn')}</Link>
  </>;

  return <>
    <div><p className="auth__eyebrow auth__eyebrow--panel">{t('auth.googleLastStep')}</p><h1 className="auth__title">{t('auth.reviewTerms')}</h1><p className="auth__sub"><strong>{pending.email}</strong> {t('auth.googleContinuing')}</p></div>
    <form className="auth__form" onSubmit={submit}>
      {error && <div className="auth__error" role="alert">{error}</div>}
      <div className="auth__account-chip"><span aria-hidden="true">G</span><div><strong>{pending.displayName}</strong><small>{pending.email}</small></div></div>
      <div className="auth__legal-checks auth__legal-checks--card">
        <Checkbox checked={kvkkRead} onChange={(e) => setKvkkRead(e.target.checked)} label={<Link to="/kvkk-aydinlatma" target="_blank">{t('auth.privacyRead')}</Link>} />
        <Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} label={<Link to="/kullanim-kosullari" target="_blank">{t('auth.termsAccepted')}</Link>} />
      </div>
      <Button type="submit" variant="primary" size="lg" loading={busy}>{t('auth.confirmAndContinue')}</Button>
      <Link className="auth__cancel" to="/giris">{t('auth.useDifferentAccount')}</Link>
    </form>
  </>;
}
